import React from 'react';
import { Link } from 'react-router-dom';
import { PageSEO } from '../../components/SEO/PageSEO';
import MarketingLayout from '../../components/marketing/MarketingLayout';
import { ShieldCheckIcon, ExclamationTriangleIcon, CheckCircleIcon, ArrowRightIcon, DocumentTextIcon, CalculatorIcon, HomeIcon, AcademicCapIcon, BriefcaseIcon, HeartIcon } from '@heroicons/react/24/outline';

const FamilyProtectionCenter: React.FC = () => {
  const bankInsurancePitfalls = [
    {
      title: "Post-Claim Underwriting",
      description: "Banks often don't fully underwrite until you claim - meaning denial when you need it most",
      impact: "Your family gets nothing after years of premiums"
    },
    {
      title: "Job Loss = Coverage Loss",
      description: "Lose your job? Your bank insurance disappears with it",
      impact: "No protection when you're most vulnerable"
    },
    {
      title: "Limited Coverage Amounts",
      description: "Bank insurance caps are often far below what families actually need",
      impact: "Mortgage paid but family still struggles"
    },
    {
      title: "No Customization",
      description: "One-size-fits-all policies that don't adapt to your family's growth",
      impact: "Gaps in coverage as your needs evolve"
    },
    {
      title: "Creditor Protection Only",
      description: "Benefits often go directly to the bank, not your family",
      impact: "No flexibility for your beneficiaries"
    }
  ];

  const privateAdvisoryAdvantages = [
    {
      title: "Full Medical Underwriting Upfront",
      description: "Know you're approved before you pay a penny - guaranteed payout when needed"
    },
    {
      title: "Portable Coverage",
      description: "Your insurance follows you through job changes, retirement, anywhere"
    },
    {
      title: "Tailored to Your Family",
      description: "Coverage that grows with you - from young family to empty nesters"
    },
    {
      title: "Tax-Optimized Strategies",
      description: "Structure policies to minimize taxes and maximize family wealth"
    },
    {
      title: "Your Dedicated Advisor",
      description: "One professional who knows your complete family situation"
    }
  ];

  const familyStages = [
    {
      stage: "Young Family",
      age: "25-35",
      icon: HomeIcon,
      needs: [
        "Replace $75,000 income if something happens",
        "Protect $350,000 mortgage from foreclosure", 
        "Secure children's education ($40,000/child)"
      ],
      risk: "One accident could mean losing the family home",
      solution: "Start with affordable term life, convert later as wealth grows"
    },
    {
      stage: "Growing Family", 
      age: "35-45",
      icon: AcademicCapIcon,
      needs: [
        "Cover rising expenses ($125,000+ income)",
        "Fund university costs (now $80,000/child)",
        "Protect against 1-in-3 disability risk"
      ],
      risk: "Disability more likely than death - yet most ignore it",
      solution: "Layer permanent insurance, add robust disability coverage"
    },
    {
      stage: "Established Family",
      age: "45-55",
      icon: BriefcaseIcon, 
      needs: [
        "Minimize taxes on $2M+ estate",
        "Protect business value for family",
        "Ensure retirement lifestyle"
      ],
      risk: "CRA could take 50% of your life's work",
      solution: "Tax-sheltered strategies, estate freeze options"
    },
    {
      stage: "Legacy Planning",
      age: "55+",
      icon: HeartIcon,
      needs: [
        "Transfer wealth tax-efficiently",
        "Support favorite charities",
        "Leave lasting family legacy"
      ],
      risk: "Grandchildren inherit tax bills, not opportunities",
      solution: "Multi-generational trusts, charitable giving strategies"
    }
  ];

  return (
    <MarketingLayout>
      <PageSEO
        title="Family Protection Center - Private Insurance Advisory | PCM"
        description="Discover why successful families choose private insurance advisory over bank insurance. Protect your family's future with bespoke life insurance, disability coverage, and comprehensive financial strategies from Paslawski Capital Management."
        keywords={[
          'family life insurance',
          'bank insurance alternatives',
          'private insurance advisor',
          'family financial protection',
          'Saskatchewan life insurance',
          'disability insurance families',
          'estate planning',
          'children education insurance'
        ]}
      />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white py-32">
          <div className="absolute inset-0 bg-black opacity-30"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <p className="text-amber-400 text-sm tracking-[0.2em] uppercase mb-6 font-medium">Family Protection Center</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-8 leading-[1.1] tracking-tight">
                Your Family Deserves More<br />
                Than Bank Insurance
              </h1>
              <p className="text-xl text-gray-300 font-light mb-12 max-w-3xl mx-auto leading-relaxed">
                While banks offer convenience, successful families need sophisticated protection strategies. 
                Discover how private insurance advisory delivers certainty, flexibility, and true financial security.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link 
                  to="/contact" 
                  className="inline-block bg-amber-500 hover:bg-amber-600 text-white font-medium py-5 px-12 rounded-md transition duration-300 text-center tracking-wide shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Schedule Family Consultation
                </Link>
                <a 
                  href="#bank-vs-private"
                  className="inline-block bg-transparent border-2 border-white hover:bg-white hover:text-slate-900 text-white font-medium py-5 px-12 rounded-md transition duration-300 text-center tracking-wide"
                >
                  See the Difference
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Horror Story Alert */}
        <section className="py-16 bg-gradient-to-br from-orange-50 to-amber-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="relative bg-white rounded-2xl shadow-xl p-8 md:p-10">
                {/* Speech bubble tail */}
                <div className="absolute -top-4 left-12 w-8 h-8 bg-white transform rotate-45"></div>
                
                <div className="relative">
                  <svg className="h-8 w-8 text-orange-400 mb-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                  
                  <p className="text-xl text-gray-800 font-medium leading-relaxed mb-4">
                    My husband died at 52. The bank denied our mortgage insurance claim because of his high blood pressure - 
                    diagnosed AFTER we got the policy. $1,800/year for nothing.
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-700 font-semibold">Lisa Thompson</p>
                      <p className="text-sm text-gray-500">Saskatoon, SK</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-orange-600">$0</p>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Claim Paid</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-12 bg-gray-50 border-b border-gray-200">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap items-center justify-center gap-12 text-sm text-gray-600">
              <div className="flex items-center gap-3">
                <ShieldCheckIcon className="h-6 w-6 text-amber-600" />
                <span className="font-medium">500+ Families Protected</span>
              </div>
              <div className="flex items-center gap-3">
                <DocumentTextIcon className="h-6 w-6 text-amber-600" />
                <span className="font-medium">100% Claims Paid</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="h-6 w-6 text-amber-600" />
                <span className="font-medium">Full Underwriting Upfront</span>
              </div>
              <div className="flex items-center gap-3">
                <CalculatorIcon className="h-6 w-6 text-amber-600" />
                <span className="font-medium">Tax-Optimized Strategies</span>
              </div>
            </div>
          </div>
        </section>

        {/* Bank Insurance Truth Section */}
        <section id="bank-vs-private" className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-20">
                <h2 className="text-3xl md:text-4xl font-light mb-6">
                  The Bank Insurance Truth
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  What your bank doesn't tell you about their insurance could cost your family everything
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 mb-20">
                {/* Bank Insurance Pitfalls */}
                <div className="bg-red-50 rounded-xl p-10 shadow-sm">
                  <h3 className="text-2xl font-semibold mb-8 text-red-900 flex items-center">
                    <ExclamationTriangleIcon className="h-8 w-8 mr-3" />
                    Bank Insurance Reality
                  </h3>
                  <div className="space-y-8">
                    {bankInsurancePitfalls.map((pitfall, index) => (
                      <div key={index} className="border-l-4 border-red-500 pl-6">
                        <h4 className="font-semibold text-red-800 mb-2">{pitfall.title}</h4>
                        <p className="text-gray-700 mb-3 leading-relaxed">{pitfall.description}</p>
                        <p className="text-sm text-red-600 font-medium">Impact: {pitfall.impact}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Private Advisory Advantages */}
                <div className="bg-green-50 rounded-xl p-10 shadow-sm">
                  <h3 className="text-2xl font-semibold mb-8 text-green-900 flex items-center">
                    <CheckCircleIcon className="h-8 w-8 mr-3" />
                    Private Advisory Advantage
                  </h3>
                  <div className="space-y-8">
                    {privateAdvisoryAdvantages.map((advantage, index) => (
                      <div key={index} className="border-l-4 border-green-500 pl-6">
                        <h4 className="font-semibold text-green-800 mb-2">{advantage.title}</h4>
                        <p className="text-gray-700 leading-relaxed">{advantage.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Success Story */}
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-12 shadow-sm">
                <div className="max-w-2xl mx-auto">
                  <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto mb-6" />
                  <p className="text-lg text-gray-700 italic mb-6 leading-relaxed">
                    "PCM found a heart murmur during my medical exam. They got me approved anyway 
                    with a different carrier. If I'd gone with bank insurance, I'd have found out 
                    after it was too late."
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <p className="text-green-700 font-semibold">Mark Davidson</p>
                    <span className="text-gray-400">•</span>
                    <p className="text-gray-600">Protected since 2019</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Family Lifecycle Protection */}
        <section className="py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-20">
                <h2 className="text-3xl md:text-4xl font-light mb-6">
                  Your Advisor for Life's Every Chapter
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  From first home to grandchildren, one trusted relationship through it all
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-10 relative">
                {/* Connection line on larger screens */}
                <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-200 via-amber-300 to-amber-200 transform -translate-x-1/2 -z-10"></div>
                
                {familyStages.map((stage, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group">
                    {/* Header with Icon */}
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 pb-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className="bg-white p-3 rounded-full shadow-md">
                            <stage.icon className="h-8 w-8 text-amber-600" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-800">{stage.stage}</h3>
                        </div>
                        <span className="text-amber-700 font-medium bg-white px-3 py-1 rounded-full text-sm shadow-sm">{stage.age}</span>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-8 pt-6">
                      {/* Needs */}
                      <div className="mb-6">
                        <p className="text-xs text-gray-500 mb-3 uppercase tracking-wider font-semibold">Protection Priorities:</p>
                        <ul className="space-y-2">
                          {stage.needs.map((need, idx) => (
                            <li key={idx} className="text-gray-700 flex items-start text-sm">
                              <CheckCircleIcon className="h-4 w-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                              <span className="leading-relaxed">{need}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Risk Warning */}
                      <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-6">
                        <p className="text-sm text-red-700 flex items-start">
                          <ExclamationTriangleIcon className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="font-medium">{stage.risk}</span>
                        </p>
                      </div>
                      
                      {/* Solution */}
                      <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                        <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider font-semibold">PCM Solution:</p>
                        <p className="text-sm text-green-800 font-medium">{stage.solution}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* CTA */}
              <div className="mt-16 text-center">
                <p className="text-lg text-gray-700 mb-8">
                  See where you fit? Let's design your family's protection roadmap.
                </p>
                <Link 
                  to="/contact" 
                  className="inline-block bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-medium py-4 px-10 rounded-md shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  Schedule Your Family Review
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Services for Families */}
        <section className="py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-light mb-20 text-center">
                Comprehensive Family Protection Services
              </h2>

              <div className="grid lg:grid-cols-3 gap-12">
                {/* Life Insurance */}
                <div className="text-center group">
                  <div className="bg-gradient-to-br from-amber-100 to-amber-200 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-8 shadow-md group-hover:shadow-lg transition-shadow">
                    <ShieldCheckIcon className="h-12 w-12 text-amber-700" />
                  </div>
                  <h3 className="text-xl font-semibold mb-6">Life Insurance Architecture</h3>
                  <ul className="text-gray-600 space-y-3 mb-8 text-left max-w-xs mx-auto">
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Term life for maximum coverage</li>
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Permanent insurance for estate needs</li>
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Conversion strategies</li>
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Beneficiary optimization</li>
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Trust integration</li>
                  </ul>
                  <Link to="/services/term-life-insurance" className="text-amber-600 font-medium hover:text-amber-700 inline-flex items-center gap-2 group/link">
                    <span>Learn More</span> 
                    <ArrowRightIcon className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>

                {/* Disability Protection */}
                <div className="text-center group">
                  <div className="bg-gradient-to-br from-amber-100 to-amber-200 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-8 shadow-md group-hover:shadow-lg transition-shadow">
                    <DocumentTextIcon className="h-12 w-12 text-amber-700" />
                  </div>
                  <h3 className="text-xl font-semibold mb-6">Income Protection</h3>
                  <ul className="text-gray-600 space-y-3 mb-8 text-left max-w-xs mx-auto">
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Disability insurance design</li>
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Critical illness coverage</li>
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Income replacement strategies</li>
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Return-to-work benefits</li>
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Family expense protection</li>
                  </ul>
                  <Link to="/services/disability-insurance" className="text-amber-600 font-medium hover:text-amber-700 inline-flex items-center gap-2 group/link">
                    <span>Learn More</span> 
                    <ArrowRightIcon className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>

                {/* Wealth Strategies */}
                <div className="text-center group">
                  <div className="bg-gradient-to-br from-amber-100 to-amber-200 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-8 shadow-md group-hover:shadow-lg transition-shadow">
                    <CalculatorIcon className="h-12 w-12 text-amber-700" />
                  </div>
                  <h3 className="text-xl font-semibold mb-6">Family Wealth Strategies</h3>
                  <ul className="text-gray-600 space-y-3 mb-8 text-left max-w-xs mx-auto">
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Education funding plans</li>
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Tax-efficient transfers</li>
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Multi-generational planning</li>
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Charitable giving strategies</li>
                    <li className="flex items-start"><span className="text-amber-500 mr-2 mt-0.5">•</span> Estate preservation</li>
                  </ul>
                  <Link to="/services/estate-preservation" className="text-amber-600 font-medium hover:text-amber-700 inline-flex items-center gap-2 group/link">
                    <span>Learn More</span> 
                    <ArrowRightIcon className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How Private Advisory Works */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-light mb-20 text-center">
                Your Family's Private Advisory Experience
              </h2>

              <div className="space-y-10">
                <div className="flex items-start gap-8">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-full w-14 h-14 flex items-center justify-center flex-shrink-0 font-semibold text-lg shadow-md">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3">Confidential Family Discovery</h3>
                    <p className="text-gray-600 leading-relaxed">
                      90-minute comprehensive review of your family's current protection, goals, and concerns. 
                      We analyze existing coverage, identify gaps, and understand your vision for your family's future.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-8">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-full w-14 h-14 flex items-center justify-center flex-shrink-0 font-semibold text-lg shadow-md">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3">Custom Protection Design</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Your advisor creates a tailored strategy addressing immediate needs and long-term goals. 
                      Every recommendation is tax-optimized and coordinated with your other professionals.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-8">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-full w-14 h-14 flex items-center justify-center flex-shrink-0 font-semibold text-lg shadow-md">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3">Full Underwriting & Approval</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Complete medical underwriting before you commit. Know with certainty that your family 
                      is protected - no surprises, no post-claim denials.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-8">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-full w-14 h-14 flex items-center justify-center flex-shrink-0 font-semibold text-lg shadow-md">
                    4
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3">Ongoing Partnership</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Annual reviews ensure your protection evolves with your family. Direct access to your 
                      advisor for life changes, questions, or claims assistance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Calculator CTA */}
        <section className="py-20 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <CalculatorIcon className="h-20 w-20 mx-auto mb-8 drop-shadow-lg" />
              <h3 className="text-3xl font-light mb-6">
                How Much Life Insurance Does Your Family Need?
              </h3>
              <p className="text-xl mb-10 text-amber-100 max-w-2xl mx-auto leading-relaxed">
                Most families are underinsured by 50% or more. Use our calculator to see if you have enough.
              </p>
              <Link 
                to="/calculators/life-insurance-needs" 
                className="inline-block bg-white text-amber-600 hover:bg-gray-100 font-medium py-5 px-12 rounded-md shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
              >
                Calculate Your Needs
              </Link>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-light mb-8">
                Your Family's Security Can't Wait
              </h2>
              <p className="text-xl mb-12 text-gray-300 font-light max-w-3xl mx-auto leading-relaxed">
                Every day without proper protection is a risk. Discover how private advisory 
                delivers the certainty your family deserves.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link 
                  to="/contact" 
                  className="inline-block bg-amber-500 hover:bg-amber-600 text-white font-medium py-5 px-14 rounded-md shadow-lg hover:shadow-xl transition-all duration-300 tracking-wide transform hover:-translate-y-0.5"
                >
                  Schedule Family Consultation
                </Link>
                <a 
                  href="tel:1-306-123-4567"
                  className="inline-block bg-transparent border-2 border-white hover:bg-white hover:text-slate-900 text-white font-medium py-5 px-14 rounded-md transition-all duration-300 tracking-wide"
                >
                  Call 1-306-123-4567
                </a>
              </div>
              <p className="mt-10 text-gray-400 text-sm">
                Virtual and in-person consultations available
              </p>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default FamilyProtectionCenter;